import { translate } from 'components/Translation';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import React, { useCallback, useEffect, useState } from 'react';
import CustomInput from 'components/CustomInput/CustomInput';
import { Caption } from 'components/Typography/Typography';
import CustomColorPicker from 'components/CustomColorPicker/CustomColorPicker';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/departmentDetailsStyle';
import { makeStyles, useTheme } from '@material-ui/core';
import Delete from '@material-ui/icons/DeleteOutlined';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { COLORS } from 'views/Organizations/const/colors';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IEditDepartmentDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	showConfirmationDialog: () => void;
	editDepartment: (name: string, uiColor: string) => void;
	departmentData: SingleDepartment;
}

const EditDepartmentDialog = ({
	isOpen,
	hideDialog,
	showConfirmationDialog,
	editDepartment,
	departmentData,
}: IEditDepartmentDialogProps) => {
	const [departmentName, setDepartmentName] = useState<string>(departmentData.name);
	const [selectedColor, setColor] = useState<string>(departmentData.uiColor);
	const classes = useStyles();
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.down('xs'));

	const clearEditDepartmentDialog = useCallback(() => {
		setColor(departmentData.uiColor);
		setDepartmentName(departmentData.name);
	}, [departmentData]);

	const deleteClicked = () => {
		hideDialog();
		showConfirmationDialog();
	};

	const cancelClicked = () => {
		hideDialog();
		clearEditDepartmentDialog();
	};

	const editDepartmentClicked = () => {
		hideDialog();
		editDepartment(departmentName, selectedColor);
		clearEditDepartmentDialog();
	};

	useEffect(() => {
		clearEditDepartmentDialog();
	}, [clearEditDepartmentDialog]);

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={cancelClicked}
			header={translate('departments.edit.department')}
			rightSideButtons={[
				{
					text: translate('cancel'),
					onClick: cancelClicked,
					color: 'tertiary-light',
				},
				{
					text: translate('save'),
					onClick: editDepartmentClicked,
					color: 'primary-light',
					disabled: departmentName.length <= 0,
				},
			]}
			leftSideButtons={[
				{
					text: matches ? <Delete /> : translate('departments.delete.department'),
					id: 'delete-department-button',
					buttonProps: { justIcon: matches },
					onClick: deleteClicked,
					color: 'tertiary-light',
				},
			]}
			dialogBody={
				<div className={classes.dialogBody}>
					<CustomInput
						labelText={translate('departments.department.name')}
						formControlProps={{
							fullWidth: true,
						}}
						id="department-name-input"
						inputProps={{
							value: departmentName,
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								setDepartmentName((event.target as HTMLInputElement).value),
						}}
					/>
					<div className={classes.colorsCaption}>
						<Caption color="secondary-light">{translate('departments.department.color')}</Caption>
					</div>
					<CustomColorPicker colors={COLORS} selectedColor={selectedColor} onChange={setColor} />
				</div>
			}
		/>
	);
};

export default EditDepartmentDialog;
